// src/wiring/manifest/goalbuddyAtomicRunsRuntime.creator.ts
//
// GoalBuddy AtomicRuns Runtime (creator)
// -------------------------------------
// Installs a runtime service that emits:
// - TimelineSurfaceEvents.emitAtomicRunsUpdate()  (detail: { sessionId, runs, source, note })
//
// v0.5: local-store driven hydration (subscribeTaskRuns)
// Later: Firestore onSnapshot / agent generation (same event shape)
//
// IMPORTANT:
// - This manifest MUST match CapabilityManifest exactly.
// - This capability is a transformer: it maps GoalBuddy store rows -> AtomicRuns packets.
//

import type { CapabilityManifest } from "@/contracts/capabilities/CapabilityManifest";
import { subscribeTaskRuns } from "@/lib/goalbuddyTaskRunStore";
import { emitAtomicRunsUpdate } from "@/bridge/goalbuddy/emitAtomicRunsUpdate";

const CAP_ID = "goalbuddy.atomicRuns.runtime" as const;

function safeString(x: unknown): string {
  return String(x ?? "").trim();
}

function getBoundSessionId(): string | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w: any = window as any;

  const sid =
    (typeof w?.__oc_debug?.sessionId === "string" && w.__oc_debug.sessionId.trim()) ||
    (typeof w?.__oc_debug?.projectId === "string" && w.__oc_debug.projectId.trim()) ||
    null;

  return sid ? String(sid) : null;
}

function getBoundUid(): string | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w: any = window as any;

  // Preferred: auth layer can set this
  const uid =
    (typeof w?.__synk_uid === "string" && w.__synk_uid.trim()) ||
    (typeof w?.__oc_debug?.uid === "string" && w.__oc_debug.uid.trim()) ||
    null;

  return uid ? String(uid) : null;
}

/**
 * Map TaskRunV1 rows to AtomicRun-ish rows (bridge stays decoupled; reducer coercion hardens).
 * Keep fields minimal and stable.
 *
 * NOTE: time fields are placeholders until time-mapping is wired.
 */
function mapTaskRunsToAtomicRuns(rows: any[]): any[] {
  const list = Array.isArray(rows) ? rows : [];

  return list
    .map((r) => {
      const id = safeString(r?.id);
      if (!id) return null;

      // Placeholder time mapping (intentionally deterministic):
      // - startSec: 0 (until mapped into a real timeline)
      // - durSec:  7 minutes (matches ATR discharge constraint)
      const startSec = 0;
      const durSec = 7 * 60;

      const label = safeString(r?.title) || safeString(r?.taskId) || `Run ${id}`;

      return {
        id,
        startSec,
        durSec,
        label,

        // Optional extras tolerated by reducer coercion:
        status: safeString(r?.status) || "planned",
        ownerId: safeString(r?.uid) || undefined,
        sessionId: safeString(r?.sid) || undefined,
        notes: r?.notes ? { ...r.notes } : undefined,

        updatedAt: typeof r?.updatedAt === "number" ? r.updatedAt : Date.now(),
        v: 1,
      };
    })
    .filter(Boolean);
}

export function createGoalBuddyAtomicRunsRuntimeManifest(): CapabilityManifest {
  return {
    id: CAP_ID,
    version: "0.5.0",
    safety: "transformer",
    tier: "creator",
    requires: [],
    provides: ["goalbuddy.atomicRuns.bridge.v0_5"],
    billing: { metered: false },

    install(): () => void {
      // Guard SSR
      if (typeof window === "undefined") return () => {};

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w: any = window as any;

      // Idempotency marker (prevents double-subscribe under HMR/remount)
      w.__synk_capabilities = w.__synk_capabilities || {};
      if (w.__synk_capabilities.goalbuddyAtomicRunsRuntimeInstalled) {
        return () => {};
      }
      w.__synk_capabilities.goalbuddyAtomicRunsRuntimeInstalled = true;

      const uid = getBoundUid() ?? "dev";
      const sid = getBoundSessionId() ?? "dev-session";

      const unsub = subscribeTaskRuns(uid, sid, (rows) => {
        emitAtomicRunsUpdate({
          sessionId: sid,
          runs: mapTaskRunsToAtomicRuns(rows),

          // ✅ policy: store-driven hydration is "system"
          source: "system",

          note: "goalbuddyTaskRunStore.subscribeTaskRuns -> atomicRuns.update",
        });
      });

      return () => {
        try {
          unsub?.();
        } catch {
          // ignore
        }

        try {
          if (w.__synk_capabilities) delete w.__synk_capabilities.goalbuddyAtomicRunsRuntimeInstalled;
        } catch {
          // ignore
        }
      };
    },
  };
}
